import { useState } from 'react';

import { Button } from '@components/button';
import { IconExclamation } from '@components/icons/exclamation';
import { hideModal, Modal, useModal } from '@components/modal';
import { hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { capitalizeFirstLetter } from '@utils/strings';

import { useInvalidateAuthentication } from 'api/authentication/hooks';
import { UserRole } from 'api/users/requests';

type RoleChangeModalProps = {
  newRole: UserRole;
  onConfirm: () => Promise<void>;
};

export const useRoleChangeModal = () => {
  return useModal(RoleChangeModal, { variant: 'REGULAR' });
};

const RoleChangeModal = ({ newRole, onConfirm }: RoleChangeModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [isLoading, setIsLoading] = useState(false);
  const { invalidateCurrentUser } = useInvalidateAuthentication();

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      await invalidateCurrentUser();
      showSuccess(t('roleChanged'));
      hideModal();
      hideSidebar();
    } catch {
      setIsLoading(false);
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} icon={<IconExclamation />} color="elementOrange100" />
      <Modal.Content>
        <Stack space={4}>
          <Text text={t('description')} />
          <Text
            text={t('newRoleMessage', { role: capitalizeFirstLetter(newRole) })}
            font="semibold14"
          />
          <Text text={t('warning')} color="textHelper" />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading}
            text={t('confirm')}
          />
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Change your role?',
    description: 'You are about to change your own role.',
    newRoleMessage: 'Your new role will be: {role}',
    warning:
      'This change will take effect immediately and may limit your access to certain features.',
    confirm: 'Confirm',
    roleChanged: 'Role changed successfully',
  },
};
